import React, { useCallback, useEffect, useState } from "react";
import { Nav, Row, Col, Tab } from "react-bootstrap";
import styled from "styled-components";
import { FaShoppingCart } from "react-icons/fa";
import { ImTree } from "react-icons/im";
import { RiSettings5Fill } from "react-icons/ri";
import { SiHomeassistantcommunitystore } from "react-icons/si";
import TabMenues from "./TabMenues";
import BuyTabs from "../buy-tab";
import DistributeTab from "../distribute-tab";
import ProductTab from "../product-tab";
import StoreTab from "../store-tab";
import UsersTab from "../users-tab";
import { getProductBuys, getProductStatistics } from "network/apis/BuyApi";
import { getDistributes } from "network/apis/DistributeApi";
import { HDivider } from "components/CommonWidgets";
import { useQuery, useQueryClient } from "react-query";
import { useProvinceSelector, useProvinceDispatch } from 'province-app/redux/province.store';
import { resetAllFilters } from 'province-app/redux/filter.slice';
import { getProducts } from "network/apis/ProductApi";
import { getStores } from "network/apis/StoreApi";
import useScreen from 'hooks/useScreen';

const MainCard = styled.div`
  background-color: #f6f6f6;
  position: relative;  
  border-top-left-radius: 36px;
  border-top-right-radius: 36px;
  margin: 5rem 0;
`;

const TabsCard = styled.div`
  background-color: white;
  position: absolute;
  width: 80%;
  top: -3rem;
  left: 0;
  right: 0;
  margin: 0 auto;
  border-radius: 33px;
  border: 1px solid #c6c6c6;
`;

const TabColumn = styled(Col)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  a {
    color: black;
    font-weight: 900;
    font-size: 14pt;
  }
`;

const TabLine = styled.div`
  width: 10em;
  height: 0.5rem;
  background-color: #0278ee;
  border-top-left-radius: 11px;
  border-top-right-radius: 11px;
`;

const TabContent = styled(Tab.Content)`
  width: 100%;
  margin: ${(props) => props.t_margin ?? '0'} 0 20rem 0;  
  padding: 0 ${(props) => props.x_padding} 0 ${(props) => props.x_padding};
  transform: translate(
    0,
    ${(props) => (props.t_offset ? props.t_offset : 0)}px
  );
  transition: all 500ms ease-in-out;
`;

const HomeTabs = ({ tabContentXPadding = '1rem' }) => {
  const queryClient = useQueryClient();
  const filterState = useProvinceSelector((store) => store.filter);
  const dispatch = useProvinceDispatch();

  const { lessThan1050, lessThan900, lessThan768, lessThan600 } = useScreen();

  const [currentTab, setCurrentTab] = useState("buy");
  const [currentTabData, setCurrentTabData] = useState();
  const [currentTopMargin, setCurrentTopMargin] = useState(0);
  const [refreshCount, setRefreshCount] = useState(0);

  useEffect(() => {
    let topOffset = 0;
    if (lessThan600) {
      topOffset = 380;
    }
    else if (lessThan768) {
      topOffset = 350;
    } else if (lessThan1050) {
      topOffset = 70;
    } else {
      topOffset = 0;
    }

    if (currentTab === 'store' && currentTopMargin > 0) {
      topOffset = topOffset / 1.9;
    }

    if (currentTab === 'users') {      
      topOffset = 0;
    }

    setCurrentTopMargin(topOffset);
  }, [currentTab, lessThan1050, lessThan768, lessThan600]);

  const toggleTopMargin = useCallback(
    (toggle) => {
      // let openOffset = lessThan768 ? 420 : 155;
      // let closeOffset = lessThan768 ? 350 : 0;

      let openOffset = 0;
      let closeOffset = 0;
      if (lessThan600) {
        openOffset = 850;
        closeOffset = 380;
      }
      else if (lessThan768) {
        openOffset = 830;
        closeOffset = 350;
      } else if (lessThan900) {
        openOffset = 600;
        closeOffset = 70;
      } else {
        openOffset = 155;
        closeOffset = 0;
      }

      setCurrentTopMargin(toggle ? openOffset : closeOffset);
    },
    [setCurrentTopMargin, lessThan600, lessThan768, lessThan900]
  );

  const {
    data: buysData,
    refetch: refetchBuys,
    isLoading: buysLoading,
  } = useQuery(
    ["province-get-product-buys", filterState.filterQuery],
    () => getProductBuys(filterState.filterQuery),
    {
      refetchOnWindowFocus: false,
      enabled: currentTab === 'buy',
    }
  );

  const {
    data: distsData,
    refetch: refetchDists,
    isLoading: distsLoading,
  } = useQuery(
    ["province-get-dists", filterState.filterQuery],
    () => getDistributes(filterState.filterQuery),
    {
      refetchOnWindowFocus: false,
      enabled: currentTab === 'distribute',
    }
  );

  const {
    data: productsData,
    refetch: refetchProducts,
    isLoading: productsLoading,
  } = useQuery(
    ["province-get-products", filterState.productsFilterQuery],
    () => getProducts(filterState.productsFilterQuery),
    {
      refetchOnWindowFocus: false,
      enabled: currentTab === 'product',
    }
  );

  const {
    data: storeData,
    refetch: refetchStore,
    isLoading: storesLoading,
  } = useQuery(
    ["province-get-stores", filterState.storesFilterQuery],
    () => getStores(filterState.storesFilterQuery),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    const statistics = storeData?.data?.statistics;
    if (statistics && currentTab === 'store') setCurrentTabData(statistics);
  }, [storeData?.data, currentTab]);

  const tabMenues = useCallback(
    () => (
      <TabMenues
        key={refreshCount}
        currentTab={currentTab}
        currentData={currentTabData}
        onCollapse={toggleTopMargin}
        onCreateSuccess={() => {
          if (currentTab === "buy") {
            refetchBuys();
            queryClient.invalidateQueries(['province-get-product-statistics']);
          }
          if (currentTab === "distribute") {
            refetchDists();
            queryClient.invalidateQueries(['get-dist-statistics']);
          }
          if (currentTab === "product") {
            refetchProducts();
            queryClient.invalidateQueries(['province-get-product-statistics']);
          }
        }}
      />
    ),
    [
      currentTabData,
      currentTab,
      toggleTopMargin,
      refetchBuys,
      refetchDists,
      refetchProducts,
      queryClient,
      refreshCount,
    ]
  );

  return (
    <MainCard>
      <Tab.Container
        defaultActiveKey={currentTab}
        onSelect={(k) => {
          dispatch(resetAllFilters());
          toggleTopMargin(false);
          setCurrentTabData(null);

          setCurrentTab(k);

          if (k === 'store') refetchStore();
        }}
      >
        <TabsCard className="pt-4">
          <Nav>
            <Row className="w-100">
              <Col md={1} />
            <TabColumn md={2}>
                <SiHomeassistantcommunitystore size={26} />
                <Nav.Item>
                  <Nav.Link eventKey={"users"}>مدیریت کاربران</Nav.Link>
                </Nav.Item>
                {currentTab === "users" && <TabLine />}
              </TabColumn>
              <TabColumn md={2}>
                <SiHomeassistantcommunitystore size={26} />
                <Nav.Item>
                  <Nav.Link eventKey={"store"}>انبار</Nav.Link>
                </Nav.Item>
                {currentTab === "store" && <TabLine />}
              </TabColumn>
              <TabColumn md={2}>
                <RiSettings5Fill size={26} />
                <Nav.Item>
                  <Nav.Link eventKey={"product"}>تولید</Nav.Link>
                </Nav.Item>
                {currentTab === "product" && <TabLine />}
              </TabColumn>
              <TabColumn md={2}>
                <ImTree size={26} />
                <Nav.Item>
                  <Nav.Link eventKey={"distribute"}>توزیع</Nav.Link>
                </Nav.Item>
                {currentTab === "distribute" && <TabLine />}
              </TabColumn>
              <TabColumn md={2}>
                <FaShoppingCart size={26} />
                <Nav.Item>
                  <Nav.Link eventKey={"buy"}>خرید</Nav.Link>
                </Nav.Item>
                {currentTab === "buy" && <TabLine />}
              </TabColumn>
              <Col md={1} />
            </Row>
            {currentTab !== 'users' && <HDivider />}
          </Nav>
          {tabMenues()}
        </TabsCard>
        <Row>
        <TabContent
          t_offset={currentTab === 'users' ? 0 : currentTopMargin }
          t_margin={currentTab != 'users' ? '14.5rem' : '8rem'}
          x_padding={tabContentXPadding}          
        >
            <Tab.Pane eventKey="buy">
              <BuyTabs
                buysData={buysData}
                isLoading={buysLoading}
                refetch={refetchBuys}
                onEditTap={(editData) => {
                  setCurrentTabData(editData);
                  toggleTopMargin(true);
                }}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="distribute">
              <DistributeTab
                distsData={distsData}
                isLoading={distsLoading}
                refetch={refetchDists}
                onEditTap={(editData) => {
                  setCurrentTabData(editData);
                  toggleTopMargin(true);
                }}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="product">
              <ProductTab
                productsData={productsData}
                isLoading={productsLoading}
                refetch={refetchProducts}
                onEditTap={(editData) => {
                  setCurrentTabData(editData);
                  setRefreshCount((prev) => prev + 1);
                }}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="store">
              <StoreTab
                storeData={storeData}
                isLoading={storesLoading}
              />
            </Tab.Pane>
            <Tab.Pane eventKey="users">
              <UsersTab />
            </Tab.Pane>
          </TabContent>
        </Row>
      </Tab.Container>
    </MainCard>
  );
};

export default HomeTabs;
